import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Upload, Download, FileText, Loader2, CheckCircle2,
  AlertCircle, LogOut, Shield, Clock, FolderOpen, X,
  File, FileImage, FileType, Trash2, RefreshCw, Eye
} from 'lucide-react';
import { authApi } from '../services/authService';
import { documentApi } from '../services/documentService';

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];

const getFileIcon = (mimeType) => {
  if (mimeType?.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
  if (mimeType?.includes('image')) return <FileImage className="w-8 h-8 text-blue-500" />;
  if (mimeType?.includes('word') || mimeType?.includes('document')) return <FileType className="w-8 h-8 text-blue-700" />;
  return <File className="w-8 h-8 text-gray-500" />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return 'N/A';
  const size = parseInt(bytes);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ClientDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upload');
  
  // Upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Documents state
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [downloadableDocs, setDownloadableDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    const loadUser = async () => {
      const currentUser = await authApi.getCurrentUser();
      if (!currentUser) {
        navigate('/');
        return;
      }
      setUser(currentUser);
      setLoading(false);
    };
    loadUser();
  }, [navigate]);

  const loadDocuments = useCallback(async () => {
    if (!user) return;
    setDocsLoading(true);
    
    const [uploadsRes, downloadsRes] = await Promise.all([
      documentApi.listUploadedDocuments(),
      documentApi.listDownloadableDocuments()
    ]);
    
    if (uploadsRes.success) setUploadedDocs(uploadsRes.documents);
    if (downloadsRes.success) setDownloadableDocs(downloadsRes.documents);
    
    setDocsLoading(false);
  }, [user]);

  useEffect(() => {
    if (user) {
      loadDocuments();
    }
  }, [user, loadDocuments]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return ALLOWED_EXTENSIONS.includes(ext);
    });
    
    if (validFiles.length !== files.length) {
      alert('Some files were rejected. Only PDF, DOC, DOCX, JPG, and PNG files are allowed.');
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
    setUploadResult(null);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);
    
    documentApi.setProgressCallback(setUploadProgress);
    
    const result = await documentApi.uploadDocuments(selectedFiles);
    
    setUploading(false);
    setUploadResult(result);
    
    if (result.success) {
      setSelectedFiles([]);
      loadDocuments();
    }
  };

  const handleDownload = async (doc) => {
    setDownloadingId(doc.id);
    await documentApi.downloadDocument(doc.id, doc.name);
    setDownloadingId(null);
  };

  const handleLogout = () => {
    authApi.logout();
    onLogout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-soft flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-secondary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-soft pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-[2rem] p-8 shadow-lg mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center text-white mr-6">
                <User size={32} />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-primary">
                  Welcome, {user?.firstName}!
                </h1>
                <div className="flex items-center mt-2 text-gray-500">
                  <Shield className="w-4 h-4 mr-2 text-secondary" />
                  <span className="font-bold text-sm">Client ID: </span>
                  <span className="ml-2 font-mono bg-gray-100 px-3 py-1 rounded-lg text-primary font-bold">
                    {user?.clientId || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold transition-all"
              data-testid="logout-btn"
            >
              <LogOut size={20} className="mr-2" />
              Logout
            </button>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex-1 flex items-center justify-center gap-3 py-4 px-6 rounded-2xl font-bold text-lg transition-all ${
              activeTab === 'upload'
                ? 'bg-secondary text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="tab-upload"
          >
            <Upload size={24} />
            Upload Documents
          </button>
          <button
            onClick={() => setActiveTab('download')}
            className={`flex-1 flex items-center justify-center gap-3 py-4 px-6 rounded-2xl font-bold text-lg transition-all ${
              activeTab === 'download'
                ? 'bg-secondary text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            data-testid="tab-download"
          >
            <Download size={24} />
            Download Documents
          </button>
        </div>

        {/* Content Area */}
        <AnimatePresence mode="wait">
          {activeTab === 'upload' && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-8"
            >
              {/* Upload Section */}
              <div className="bg-white rounded-[2rem] p-8 shadow-lg">
                <h2 className="text-xl font-black text-primary mb-6 flex items-center">
                  <Upload className="w-6 h-6 mr-3 text-secondary" />
                  Upload Documents to Preparer
                </h2>
                
                {/* Drop Zone */}
                <label className="block border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center cursor-pointer hover:border-secondary hover:bg-secondary/5 transition-all">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg font-bold text-gray-600">
                    Click to select files or drag and drop
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Accepted: PDF, DOC, DOCX, JPG, PNG
                  </p>
                </label>

                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <p className="font-bold text-primary">
                        Selected Files ({selectedFiles.length})
                      </p>
                      <button
                        onClick={() => setSelectedFiles([])}
                        className="text-sm text-gray-400 hover:text-secondary"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gray-50 p-3 rounded-xl"
                        >
                          <div className="flex items-center">
                            <FileText className="w-5 h-5 text-secondary mr-3" />
                            <span className="font-medium text-gray-700 truncate max-w-xs">
                              {file.name}
                            </span>
                            <span className="ml-3 text-sm text-gray-400">
                              {formatFileSize(file.size)}
                            </span>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Progress */}
                {uploading && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-primary">Uploading...</span>
                      <span className="text-secondary font-bold">{uploadProgress}%</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadProgress}%` }}
                        className="h-full bg-secondary"
                      />
                    </div>
                  </div>
                )}

                {/* Upload Result */}
                {uploadResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mt-6 p-4 rounded-xl flex items-center ${
                      uploadResult.success
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-700'
                    }`}
                  >
                    {uploadResult.success ? (
                      <CheckCircle2 className="w-5 h-5 mr-3" />
                    ) : (
                      <AlertCircle className="w-5 h-5 mr-3" />
                    )}
                    <span className="font-bold">{uploadResult.message}</span>
                  </motion.div>
                )}

                {/* Upload Button */}
                <button
                  onClick={handleUpload}
                  disabled={selectedFiles.length === 0 || uploading}
                  className="mt-6 w-full bg-secondary hover:bg-secondary-hover disabled:bg-gray-300 text-white py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center"
                  data-testid="upload-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2" />
                      Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>

              {/* Previously Uploaded */}
              <div className="bg-white rounded-[2rem] p-8 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-black text-primary flex items-center">
                    <Clock className="w-6 h-6 mr-3 text-secondary" />
                    Your Uploaded Documents
                  </h2>
                  <button
                    onClick={loadDocuments}
                    disabled={docsLoading}
                    className="text-secondary hover:text-secondary-hover"
                  >
                    <RefreshCw className={`w-5 h-5 ${docsLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {docsLoading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 text-secondary animate-spin mx-auto" />
                  </div>
                ) : uploadedDocs.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="font-bold">No documents uploaded yet</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {uploadedDocs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between bg-gray-50 p-4 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center">
                          {getFileIcon(doc.mimeType)}
                          <div className="ml-4">
                            <p className="font-bold text-primary truncate max-w-xs">
                              {doc.name}
                            </p>
                            <p className="text-sm text-gray-400">
                              {formatFileSize(doc.size)} • {formatDate(doc.createdTime)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownload(doc)}
                          disabled={downloadingId === doc.id}
                          className="p-2 text-secondary hover:bg-secondary/10 rounded-lg transition-colors"
                        >
                          {downloadingId === doc.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'download' && (
            <motion.div
              key="download"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <div className="bg-white rounded-[2rem] p-8 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-black text-primary flex items-center">
                    <Download className="w-6 h-6 mr-3 text-secondary" />
                    Documents from Preparer
                  </h2>
                  <button
                    onClick={loadDocuments}
                    disabled={docsLoading}
                    className="text-secondary hover:text-secondary-hover"
                  >
                    <RefreshCw className={`w-5 h-5 ${docsLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {docsLoading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 text-secondary animate-spin mx-auto" />
                  </div>
                ) : downloadableDocs.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <FolderOpen className="w-20 h-20 mx-auto mb-4 opacity-30" />
                    <p className="font-bold text-lg">No documents available yet</p>
                    <p className="text-sm mt-2">
                      Your tax preparer will upload documents here for you to download.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {downloadableDocs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between bg-gray-50 p-4 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center">
                          {getFileIcon(doc.mimeType)}
                          <div className="ml-4">
                            <p className="font-bold text-primary truncate max-w-xs">
                              {doc.name}
                            </p>
                            <p className="text-sm text-gray-400">
                              {formatFileSize(doc.size)} • {formatDate(doc.createdTime)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownload(doc)}
                          disabled={downloadingId === doc.id}
                          className="flex items-center px-4 py-2 bg-secondary text-white rounded-lg font-bold hover:bg-secondary-hover transition-colors"
                          data-testid={`download-${doc.id}`}
                        >
                          {downloadingId === doc.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Security Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center text-gray-400 text-sm">
            <Shield className="w-4 h-4 mr-2" />
            <span>Your documents are securely stored and only accessible by you and your tax preparer.</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ClientDashboard;
